import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  MessageCircle,
  Eye,
  MoreHorizontal,
  MapPin,
  ChevronDown,
  ChevronUp,
  Send,
  ThumbsUp,
  ThumbsDown,
} from 'lucide-react';
import CommunityAvatar from './CommunityAvatar';
import ThreadMetaBadges from './ThreadMetaBadges';
import * as provider from '../../data/community/communityDataProvider';
import { useCommunityDataSource } from '../../utils/communityDataSource';

function formatRelativeTime(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  const diff = Date.now() - d.getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins}m`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 48) return `${hrs}h`;
  return `${Math.floor(hrs / 24)}d`;
}

function KindBadge({ kind }) {
  const regional = kind === 'regional';
  return (
    <span
      className={`text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded border ${
        regional
          ? 'border-primary/40 text-primary bg-primary/10'
          : 'border-dark-border text-dark-text-muted'
      }`}
    >
      {kind}
    </span>
  );
}

function CommentRow({ comment, isLast }) {
  return (
    <div className="relative flex gap-3 pl-1">
      <div
        className="absolute left-[19px] top-0 w-px bg-dark-border"
        style={{ bottom: isLast ? '50%' : 0 }}
        aria-hidden
      />
      <div className="relative z-[1] pt-0.5">
        <CommunityAvatar userId={comment.author_user_id} size={32} />
      </div>
      <div className="flex-1 min-w-0 pb-4">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-semibold text-dark-text text-sm">
            user_{comment.author_user_id}
          </span>
          <span className="text-dark-text-muted text-xs">
            {formatRelativeTime(comment.created_at)}
          </span>
          {comment.edited_at && (
            <span className="text-dark-text-muted text-[10px]">(edited)</span>
          )}
        </div>
        <p className="text-dark-text text-sm mt-1 whitespace-pre-wrap break-words">
          {comment.body}
        </p>
      </div>
    </div>
  );
}

function invalidateThreadQueries(queryClient, dataSource, threadId) {
  const keys = [
    ['communityThreads'],
    ['communityOverview'],
    ['communityNearby'],
    ['communityMapThreads'],
    ['communityThread', dataSource, threadId],
    ['communityComments', dataSource, threadId],
  ];
  keys.forEach((key) => queryClient.invalidateQueries({ queryKey: key }));
}

/**
 * Threads-style post with v2 community fields: votes, tags, board, GPS flags.
 */
export default function ThreadPostCard({
  thread,
  actingUserId = 1001,
  defaultExpanded = false,
  compact = false,
  onAdminDetails,
}) {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const [commentBody, setCommentBody] = useState('');
  const queryClient = useQueryClient();
  const dataSource = useCommunityDataSource();

  const displayName = thread.username || thread.author?.username || `user_${thread.author_user_id}`;

  const commentsQuery = useQuery({
    queryKey: ['communityComments', dataSource, thread.id],
    queryFn: () => provider.getComments(thread.id, { limit: 50 }),
    enabled: expanded && (thread.comment_count ?? 0) > 0,
  });

  const commentMut = useMutation({
    mutationFn: () => provider.postComment(thread.id, { body: commentBody }, actingUserId),
    onSuccess: () => {
      setCommentBody('');
      setExpanded(true);
      invalidateThreadQueries(queryClient, dataSource, thread.id);
    },
  });

  const upvoteMut = useMutation({
    mutationFn: () => provider.postVote(thread.id, 'up', actingUserId),
    onSuccess: () => invalidateThreadQueries(queryClient, dataSource, thread.id),
  });

  const downvoteMut = useMutation({
    mutationFn: () => provider.postVote(thread.id, 'down', actingUserId),
    onSuccess: () => invalidateThreadQueries(queryClient, dataSource, thread.id),
  });

  const viewMut = useMutation({
    mutationFn: () => provider.postThreadView(thread.id, actingUserId),
    onSuccess: () => invalidateThreadQueries(queryClient, dataSource, thread.id),
  });

  const comments = commentsQuery.data?.data || [];
  const replyCount = thread.comment_count ?? 0;
  const upCount = thread.up_count ?? 0;
  const downCount = thread.down_count ?? 0;
  const viewCount = thread.view_count ?? 0;
  const myVote = thread.my_vote;

  const toggleReplies = () => setExpanded((v) => !v);

  return (
    <article
      className={`border-b border-dark-border ${compact ? 'py-3 px-3' : 'py-4 px-4 sm:px-5'} bg-dark-card/30 hover:bg-dark-surface/20 transition-colors`}
    >
      <div className="flex gap-3">
        <CommunityAvatar userId={thread.author_user_id} size={compact ? 36 : 44} />
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <div className="flex items-center gap-2 flex-wrap min-w-0">
              <span className="font-semibold text-dark-text truncate">{displayName}</span>
              <KindBadge kind={thread.kind} />
              <span className="text-dark-text-muted text-xs">
                {formatRelativeTime(thread.created_at)}
              </span>
              <span className="text-dark-text-muted text-xs">#{thread.id}</span>
            </div>
            <div className="flex items-center gap-1 shrink-0">
              {onAdminDetails && (
                <button
                  type="button"
                  className="text-xs text-dark-text-muted hover:text-primary px-2 py-1"
                  onClick={() => onAdminDetails(thread.id)}
                >
                  Admin
                </button>
              )}
              <button
                type="button"
                className="p-1 text-dark-text-muted hover:text-primary rounded"
                aria-label="More"
              >
                <MoreHorizontal size={18} />
              </button>
            </div>
          </div>

          {thread.title && (
            <p className="font-medium text-dark-text mt-2 text-[15px]">{thread.title}</p>
          )}
          <p className="text-dark-text text-[15px] mt-1 whitespace-pre-wrap break-words leading-relaxed">
            {thread.body}
          </p>

          <ThreadMetaBadges thread={thread} />

          {thread.has_gps && thread.latitude != null && (
            <p className="flex items-center gap-1 text-xs text-dark-text-muted mt-2">
              <MapPin size={12} className="text-primary" />
              {thread.latitude.toFixed(4)}, {thread.longitude.toFixed(4)}
              {thread.distance_m != null && ` · ${Math.round(thread.distance_m)}m away`}
            </p>
          )}

          <div className="flex items-center gap-4 mt-3 text-dark-text-muted flex-wrap">
            <button
              type="button"
              className={`flex items-center gap-1 hover:text-green-400 transition-colors ${
                myVote === 1 ? 'text-green-400' : ''
              }`}
              onClick={() => upvoteMut.mutate()}
              disabled={upvoteMut.isPending}
              title="Upvote"
            >
              <ThumbsUp size={16} />
              <span className="text-xs tabular-nums">{upCount}</span>
            </button>
            <button
              type="button"
              className={`flex items-center gap-1 hover:text-red-400 transition-colors ${
                myVote === -1 ? 'text-red-400' : ''
              }`}
              onClick={() => downvoteMut.mutate()}
              disabled={downvoteMut.isPending}
              title="Downvote"
            >
              <ThumbsDown size={16} />
              <span className="text-xs tabular-nums">{downCount}</span>
            </button>
            <button
              type="button"
              className="flex items-center gap-1.5 hover:text-primary transition-colors"
              onClick={toggleReplies}
            >
              <MessageCircle size={18} />
              <span className="text-xs tabular-nums">{replyCount}</span>
            </button>
            <button
              type="button"
              className="flex items-center gap-1.5 hover:text-primary transition-colors"
              onClick={() => viewMut.mutate()}
              disabled={viewMut.isPending}
            >
              <Eye size={18} />
              <span className="text-xs tabular-nums">{viewCount}</span>
            </button>
          </div>

          {!expanded && replyCount > 0 && (
            <button
              type="button"
              onClick={toggleReplies}
              className="mt-3 flex items-center gap-2 text-xs text-dark-text-secondary hover:text-primary w-full text-left"
            >
              <span>
                {replyCount} {replyCount === 1 ? 'reply' : 'replies'}
                {upCount > 0 && ` · ▲${upCount}`}
              </span>
            </button>
          )}
        </div>
      </div>

      {expanded && (
        <div className="mt-2 ml-[22px] sm:ml-[26px] pl-6 border-l border-dark-border/80">
          {commentsQuery.isLoading && replyCount > 0 && (
            <p className="text-xs text-dark-text-muted py-2">Loading replies…</p>
          )}
          {comments.length > 0 && (
            <div className="pt-2">
              {comments.map((c, i) => (
                <CommentRow key={c.id} comment={c} isLast={i === comments.length - 1} />
              ))}
            </div>
          )}
          {expanded && replyCount > 0 && comments.length === 0 && !commentsQuery.isLoading && (
            <p className="text-xs text-dark-text-muted py-2">No replies yet.</p>
          )}

          <div className="flex gap-2 items-end pt-1 pb-2">
            <CommunityAvatar userId={actingUserId} size={28} />
            <div className="flex-1 flex gap-2">
              <input
                className="flex-1 bg-dark-bg border border-dark-border rounded-full px-4 py-2 text-sm text-dark-text placeholder:text-dark-text-muted"
                placeholder="Reply to thread…"
                value={commentBody}
                onChange={(e) => setCommentBody(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    if (commentBody.trim()) commentMut.mutate();
                  }
                }}
              />
              <button
                type="button"
                className="p-2 rounded-full bg-primary text-black disabled:opacity-50"
                onClick={() => commentMut.mutate()}
                disabled={commentMut.isPending || !commentBody.trim()}
                aria-label="Post reply"
              >
                <Send size={16} />
              </button>
            </div>
          </div>

          <button
            type="button"
            className="text-xs text-dark-text-muted hover:text-primary flex items-center gap-1 mb-2"
            onClick={toggleReplies}
          >
            <ChevronUp size={14} />
            Hide replies
          </button>
        </div>
      )}

      {!expanded && replyCount === 0 && (
        <button
          type="button"
          className="mt-2 ml-11 sm:ml-12 text-xs text-dark-text-muted hover:text-primary flex items-center gap-1"
          onClick={toggleReplies}
        >
          <ChevronDown size={14} />
          Add reply
        </button>
      )}
    </article>
  );
}
